/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Scale } from '../../utils/UNSAFE_visTypes/chart';

export function getResolvedDragMode(
  isZoomAndScroll: boolean,
  isMultiSelection: boolean,
  isMobile: boolean,
  dragMode: 'off' | 'pan' | 'zoom' | 'user' | 'select'
): 'zoom' | 'select' | 'pan' | 'off' {
  if (
    (!isMultiSelection && dragMode === 'select') ||
    (!isZoomAndScroll && dragMode === 'zoom') ||
    dragMode === 'off'
  ) {
    return 'off';
  }

  if (dragMode === 'user') {
    if (isMobile) {
      return isZoomAndScroll ? 'pan' : 'off';
    }

    if (!isZoomAndScroll && isMultiSelection) {
      return isMobile ? 'select' : 'off';
    }

    if (!isZoomAndScroll && !isMultiSelection) {
      return 'off';
    }

    return 'pan';
  }

  return dragMode;
}

export function isPanButtonVisible(
  isZoomAndScroll: boolean,
  dragMode: 'off' | 'pan' | 'zoom' | 'user' | 'select',
  isMobile: boolean
) {
  if (!isMobile || !isZoomAndScroll) {
    return false;
  }

  return dragMode !== 'select' && dragMode !== 'off';
}

export function isZoomButtonVisible(
  isZoomAndScroll: boolean,
  isMobile: boolean,
  dragMode: 'zoom' | 'select' | 'pan' | 'off' | 'user'
) {
  if (isMobile || !isZoomAndScroll) {
    return false;
  }

  return dragMode === 'user';
}

export function isSelectButtonVisible(
  isMobile: boolean,
  isMultiSelection: boolean,
  isZoomAndScroll: boolean,
  dragMode: 'zoom' | 'select' | 'pan' | 'user' | 'off'
) {
  if (!isMultiSelection) {
    return false;
  }
  if (!isMobile && !isZoomAndScroll && isMultiSelection) {
    return false;
  }

  return isMobile ? true : dragMode === 'user';
}

export function getMarqueeDims(
  xScale: Scale,
  initialPoint: { x: number; y: number } | undefined,
  finalPoint: { x: number; y: number } | undefined
) {
  if (initialPoint && finalPoint) {
    const x1 = Math.min(initialPoint.x, finalPoint.x);
    const x2 = Math.max(initialPoint.x, finalPoint.x);

    const y1 = Math.min(initialPoint.y, finalPoint.y);
    const y2 = Math.max(initialPoint.y, finalPoint.y);

    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);

    const delta = finalPoint.x - initialPoint.x;
    const axisDelta = xScale.invert(finalPoint.x) - xScale.invert(initialPoint.x);
    return {
      x1,
      y1,
      width,
      height,
      x2,
      y2,
      delta,
      axisDelta
    };
  }
  return;
}

export function getCursorType(
  currentDragMode: 'zoom' | 'select' | 'pan' | 'off',
  isPointerDown: boolean
) {
  if (currentDragMode === 'zoom' || currentDragMode === 'select') {
    return 'crosshair';
  }

  if (currentDragMode === 'pan') {
    return isPointerDown ? 'grabbing' : 'grab';
  }

  return;
}
